/** Sample rate the browser captures at and the transcriber expects. */
export const RADIO_SAMPLE_RATE = 16_000;

/** Size of one VAD block in samples (~50 ms at 16 kHz). */
export const RADIO_BLOCK_SAMPLES = 800;

export const RADIO_LABEL_STORAGE_KEY = "vtms.radio.label";
export const RADIO_THRESHOLD_STORAGE_KEY = "vtms.radio.thresholdDb";
export const RADIO_DEFAULT_LABEL = "pit";
export const RADIO_LABEL_MAX_LENGTH = 32;

export const RADIO_THRESHOLD_MIN_DB = -60;
export const RADIO_THRESHOLD_MAX_DB = -20;
export const RADIO_THRESHOLD_DEFAULT_DB = -45;

/** Lower bound of the level meter; anything quieter is drawn as empty. */
export const RADIO_METER_FLOOR_DB = -80;

export type RadioCaptureState =
  | "idle"
  | "connecting"
  | "listening"
  | "error"
  | "reconnecting";

export interface RadioInputDevice {
  deviceId: string;
  label: string;
}

/** One transcript as published on `{prefix}radio/transcript` and echoed on the WS. */
export interface RadioTranscript {
  text: string;
  label: string;
  durationS: number | null;
  partial: boolean;
  file: string | null;
  client: string | null;
  /** ISO timestamp from the server. */
  ts: string;
}

/** Stable identity for dedupe between the WS echo and the MQTT broadcast. */
export function radioTranscriptKey(t: RadioTranscript): string {
  return `${t.ts}|${t.client ?? ""}`;
}

function asString(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

/**
 * Parse the transcript object shared by the WS `transcript` message and the
 * MQTT payload's `value`. Returns null unless `text` and `ts` are present.
 */
export function parseRadioTranscript(raw: unknown): RadioTranscript | null {
  if (typeof raw !== "object" || raw === null) return null;
  const obj = raw as Record<string, unknown>;
  const text = asString(obj.text);
  const ts = asString(obj.ts);
  if (text === null || ts === null) return null;
  return {
    text,
    label: asString(obj.label) ?? "",
    durationS: typeof obj.duration_s === "number" ? obj.duration_s : null,
    partial: obj.partial === true,
    file: asString(obj.file),
    client: asString(obj.client),
    ts,
  };
}

/** Parse the `{prefix}radio/transcript` MQTT payload: `{"value":{...},"ts":"..."}`. */
export function parseRadioTranscriptPayload(payload: string): RadioTranscript | null {
  if (!payload) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(payload);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const outer = parsed as Record<string, unknown>;
  const value = typeof outer.value === "object" && outer.value !== null
    ? { ...(outer.value as Record<string, unknown>) }
    : null;
  if (!value) return null;
  if (typeof value.ts !== "string" && typeof outer.ts === "string") value.ts = outer.ts;
  return parseRadioTranscript(value);
}

export type RadioServerMessage =
  | { type: "ready"; model: string | null }
  | { type: "transcript"; transcript: RadioTranscript }
  | { type: "error"; message: string };

/** Parse a TEXT frame from the `/radio-audio` socket. Unknown shapes yield null. */
export function parseRadioServerMessage(data: string): RadioServerMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const obj = parsed as Record<string, unknown>;
  switch (obj.type) {
    case "ready":
      return { type: "ready", model: asString(obj.model) };
    case "transcript": {
      const transcript = parseRadioTranscript(obj);
      return transcript ? { type: "transcript", transcript } : null;
    }
    case "error":
      return { type: "error", message: asString(obj.message) ?? asString(obj.error) ?? "server error" };
    default:
      return null;
  }
}
